import { localizer } from "@stormglass/game-localization";
import { useNavigate } from "react-router-dom";

import { WeatherIcon } from "../components/WeatherIcon";
import { useGameHost, useGameState } from "../game/GameContext";

const topEntry = (
  record: Readonly<Record<string, number>>,
): readonly [string, number] | null =>
  Object.entries(record).sort((a, b) => b[1] - a[1])[0] ?? null;

export function ResultsScreen(): React.JSX.Element {
  const host = useGameHost();
  const { snapshot } = useGameState();
  const stats = host.getStatistics();
  const navigate = useNavigate();
  const weapon = topEntry(stats.damageBySource);
  const reaction = topEntry(stats.reactionsTriggered);
  const restart = async (): Promise<void> => {
    await host.unlockAudio();
    host.startFreshRun();
    void navigate("/play");
  };
  return (
    <div className="results-page page-panel">
      <p className="eyebrow">RUN // COMPLETE</p>
      <h1>
        {snapshot.endReason === "boss-deadline"
          ? localizer.t("results.bossTitle")
          : localizer.t("results.title")}
      </h1>
      <p className="page-lede">
        {localizer.t("results.summary", {
          round: stats.highestRound,
          condensate: snapshot.condensate,
        })}
      </p>
      <div className="results-hero">
        <div className="results-ring">
          <span>ROUND</span>
          <strong>{String(stats.highestRound).padStart(2, "0")}</strong>
          <small>
            {snapshot.endReason?.replaceAll("-", " ") ?? "complete"}
          </small>
        </div>
        <div className="results-record">
          <span>CONDENSATE RECOVERED</span>
          <strong>◇ {localizer.number(snapshot.condensate)}</strong>
          <small>Permanent chamber resource</small>
        </div>
      </div>
      <div className="results-grid">
        <div>
          <small>{localizer.t("results.defeated")}</small>
          <strong>{localizer.number(stats.enemiesDefeated)}</strong>
        </div>
        <div>
          <small>{localizer.t("results.bosses")}</small>
          <strong>{stats.bossesDefeated}</strong>
        </div>
        <div>
          <small>{localizer.t("results.best")}</small>
          <strong>{stats.highestRound}</strong>
        </div>
        <div className="results-grid__feature">
          <small>{localizer.t("results.weapon")}</small>
          <strong>
            <WeatherIcon kind="lightning" />
            {weapon?.[0].split(".").at(-1)?.replaceAll("-", " ") ??
              "Priority Bolt"}
          </strong>
          <span>{localizer.number(weapon?.[1] ?? 0)} damage</span>
        </div>
        <div className="results-grid__feature">
          <small>{localizer.t("results.reaction")}</small>
          <strong>
            ⌁{" "}
            {reaction?.[0].split(".").at(-1)?.replaceAll("-", " ") ??
              "Not triggered"}
          </strong>
          <span>{localizer.number(reaction?.[1] ?? 0)} activations</span>
        </div>
      </div>
      <div className="page-actions">
        <button
          className="button button--primary button--large"
          type="button"
          onClick={() => {
            void restart();
          }}
        >
          {localizer.t("action.restart")} <b>→</b>
        </button>
        <button
          className="button button--ghost"
          type="button"
          onClick={() => {
            void navigate("/shop");
          }}
        >
          Open chamber lab
        </button>
      </div>
    </div>
  );
}
